import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { paymentsApi } from './paymentsApi';
import type {
  PaymentListParams,
  PaymentPatchPayload,
  PaymentPayload,
  RefundPayload,
} from './types';

const KEY = 'payments';

/**
 * Money moving changes more than this feature's own queries.
 *
 * `appointments` carries the balance on every row, so the calendar and the
 * drawer are stale the moment a payment lands; `clients` carries the lifetime
 * spend on the profile; `financeSummary` nets the range on the finance screen. Neither row is written by this mutation, which is
 * exactly why they have to be invalidated by hand — the same reason completing
 * an appointment already invalidates `clients`.
 */
const useInvalidateMoney = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY] });
    void qc.invalidateQueries({ queryKey: ['appointments'] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
    void qc.invalidateQueries({ queryKey: ['financeSummary'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
  };
};

export const useAppointmentPayments = (appointmentId: number | null) =>
  useQuery({
    queryKey: [KEY, 'appointment', appointmentId],
    queryFn: () => paymentsApi.listForAppointment(appointmentId as number),
    enabled: appointmentId !== null && appointmentId > 0,
  });

export const usePaymentsList = (params: PaymentListParams) =>
  useQuery({
    queryKey: [KEY, 'list', params],
    queryFn: () => paymentsApi.list(params),
    // The till is read by date range, and stepping through days is the most
    // frequent interaction: without this the table is replaced by a spinner on
    // every step. Same call `useCalendar` makes.
    placeholderData: (previous) => previous,
  });

export const useRecordPayment = (appointmentId: number) => {
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: (data: PaymentPayload) => paymentsApi.record(appointmentId, data),
    onSuccess: invalidate,
  });
};

export const useRefundPayment = () => {
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: number; data: RefundPayload }) =>
      paymentsApi.refund(paymentId, data),
    onSuccess: invalidate,
  });
};

export const usePatchPayment = () => {
  const invalidate = useInvalidateMoney();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: number; data: PaymentPatchPayload }) =>
      paymentsApi.patch(paymentId, data),
    onSuccess: invalidate,
  });
};
